import { parseDateTime } from '@agroone/dates'
import { SortingRule, SortingRuleOrder, SortingRuleType } from '@agroone/entities'
import { Injectable } from '@angular/core'
import { isAfter, isEqual } from 'date-fns'
import { requireDefined } from '../utils/require-defined.util'

/**
 * Reads the property a sorting rule targets. The rule names it at runtime, so the value is untyped:
 * each sort states the type it expects, as the untyped property access it replaces did.
 */
function ruleValue<V>(entity: object, rule: SortingRule): V {
  return Reflect.get(entity, rule.propertyName)
}

/** Parses a date property value; a value that is not ISO 8601 cannot be ordered. */
function ruleDate(entity: object, rule: SortingRule): Date {
  return requireDefined(parseDateTime(ruleValue<string>(entity, rule)), `an ISO 8601 date in ${rule.propertyName}`)
}

@Injectable({
  providedIn: 'root',
})
export class SortService {
  public sort<T extends object>(entities: T[], sortingRules: SortingRule[]): T[] {
    const rules: SortingRule[] = sortingRules
    let sorted: T[] = entities

    for (const rule of rules) {
      switch (rule.type) {
        case SortingRuleType.DATE:
          sorted = this.sortByDate(entities, rule)
          break
        case SortingRuleType.BOOLEAN:
          sorted = this.sortByBoolean(entities, rule)
          break
        case SortingRuleType.NUMBER:
          sorted = this.sortByNumber(entities, rule)
          break
        case SortingRuleType.STRING:
        default:
          sorted = this.sortByASCII(entities, rule)
          break
      }
    }

    return sorted
  }

  private sortByASCII<T extends object>(entities: T[], rule: SortingRule): T[] {
    let sorted: T[]
    const [_, values] = entities.reduce<[T[], T[]]>(
      (array, entity) => {
        if (!ruleValue(entity, rule)) {
          array[0].push(entity)
        } else {
          array[1].push(entity)
        }
        return array
      },
      [[], []],
    )

    switch (rule.order) {
      case SortingRuleOrder.DESCENDING:
        sorted = values.sort((a, b) => ruleValue<string>(a, rule).localeCompare(ruleValue(b, rule)))
        break
      case SortingRuleOrder.ASCENDING:
      default:
        sorted = values.sort((a, b) => ruleValue<string>(b, rule).localeCompare(ruleValue(a, rule)))
        break
    }

    return sorted
  }

  /**
   * Smallest value first in ascending order, largest first in descending order. Entities without a numeric value
   * (absent, or not parsable when the API sends a decimal as a string) keep their relative order at the end.
   */
  private sortByNumber<T extends object>(entities: T[], rule: SortingRule): T[] {
    const numberOf = (entity: T): number | null => {
      const value = ruleValue<unknown>(entity, rule)
      const parsed = value === null || value === undefined || value === '' ? NaN : Number(value)
      return Number.isFinite(parsed) ? parsed : null
    }
    const direction = rule.order === SortingRuleOrder.DESCENDING ? -1 : 1

    return entities.sort((a, b) => {
      const x = numberOf(a)
      const y = numberOf(b)
      if (x === null || y === null) {
        return (x === null ? 1 : 0) - (y === null ? 1 : 0)
      }
      return (x - y) * direction
    })
  }

  private sortByDate<T extends object>(entities: T[], rule: SortingRule): T[] {
    let sorted: T[]
    const now: Date = new Date()
    const [nulls, future, present, past] = entities.reduce<[T[], T[], T[], T[]]>(
      (array, entity) => {
        if (!ruleValue(entity, rule)) {
          array[0].push(entity)
        } else if (isAfter(parseDateTime(ruleValue(entity, rule)) ?? NaN, now)) {
          array[1].push(entity)
        } else if (isEqual(parseDateTime(ruleValue(entity, rule)) ?? NaN, now)) {
          array[2].push(entity)
        } else {
          array[3].push(entity)
        }
        return array
      },
      [[], [], [], []],
    )

    switch (rule.order) {
      case SortingRuleOrder.ASCENDING:
        sorted = future
          .concat(present, past)
          .sort((a: T, b: T) => ruleDate(a, rule).valueOf() - ruleDate(b, rule).valueOf())
          .concat(nulls)
        break
      case SortingRuleOrder.DESCENDING:
      default:
        sorted = past
          .concat(present, future)
          .sort((a: T, b: T) => ruleDate(b, rule).valueOf() - ruleDate(a, rule).valueOf())
          .concat(nulls)
        break
    }
    return sorted
  }

  private sortByBoolean<T extends object>(entities: T[], rule: SortingRule): T[] {
    let sorted: T[]
    switch (rule.order) {
      case SortingRuleOrder.ASCENDING:
        sorted = entities.sort((a, b) => (ruleValue(a, rule) === ruleValue(b, rule) ? 0 : ruleValue(a, rule) ? -1 : 1))
        break
      case SortingRuleOrder.DESCENDING:
        sorted = entities.sort((a, b) => (ruleValue(a, rule) === ruleValue(b, rule) ? 0 : ruleValue(a, rule) ? 1 : -1))
        break
    }
    return sorted
  }
}
